import { useCallback, useMemo, useState } from 'react';

import type { Home } from './dayRoute';
import { useAccountStorage } from '../../shared/account/context';
import type { KeyValueStorage } from '../../shared/account/storage';

/**
 * Where the user is staying, in localStorage beside the schedule: MAP MY DAY
 * starts and ends every day there once it is set.
 *
 * Never asked for up front. The map offers "Start from my hotel" on a day with
 * two or more events, and nothing changes until the user takes it up.
 */

const KEY = 'mcw.home';

const isCoord = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n);

function read(storage: KeyValueStorage): Home | null {
  try {
    const raw = storage.getItem(KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const { label, coords } = parsed as Partial<Home>;
    if (typeof label !== 'string' || !Array.isArray(coords) || coords.length !== 2) return null;
    if (!isCoord(coords[0]) || !isCoord(coords[1])) return null;
    return { label, coords: [coords[0], coords[1]] };
  } catch {
    return null;
  }
}

function write(home: Home | null, storage: KeyValueStorage): void {
  try {
    if (home) storage.setItem(KEY, JSON.stringify(home));
    else storage.removeItem(KEY);
  } catch {
    // Site data blocked. The hotel still works for this session.
  }
}

export interface HomeStore {
  home: Home | null;
  /** Null forgets it. */
  setHome: (home: Home | null) => void;
}

export function useHome(): HomeStore {
  /** localStorage, or the account once the user has one. */
  const storage = useAccountStorage();
  const [home, setState] = useState<Home | null>(() => read(storage));

  // The hotel moved: start again from wherever it now lives.
  const [source, setSource] = useState(storage);
  if (source !== storage) {
    setSource(storage);
    setState(read(storage));
  }

  const setHome = useCallback((next: Home | null) => {
    write(next, storage);
    setState(next);
  }, [storage]);
  // One object while nothing changes, so the sidebars holding it need not redraw.
  return useMemo(() => ({ home, setHome }), [home, setHome]);
}
